import { useEffect, useState, useMemo, useContext, createContext } from 'react';
import {
  fetchOrder,
  fetchOrderConfig,
  fetchOrderModel,
  requestHelpByOrder,
  type Order,
  type OrderModelResponse
} from '@services/api';
import OrderHeader from './orderHeader';
import { OrderInfoTabs } from './orderInfoTabs';
import { useAuthContext } from '@app/authContext';
import { FormRootConfig } from 'types/form';
import { useAccurateContext } from '@utils/useAccurateContext';
import CustomerLink from './customerLink';
import ProductBgLink from './productBgLink';
import { Col, message, Modal, Row, Space } from 'antd';
import { useRealmRoles } from '@app/hooks/useRealmRoles';

interface Props {
  orderId: string;
}

interface OrderFormContextI {
  order: Order | null;
  model: OrderModelResponse | null;
  config: FormRootConfig | null;
  reloadOrder: () => Promise<void>;
  requestHelp?: () => void;
}
const OrderFormContext = createContext<OrderFormContextI | null>(null);
export const useOrderFormContext = () => useAccurateContext(OrderFormContext);

export default function OrderInfo({ orderId }: Props) {
  const { auth } = useAuthContext();
  const accessToken = auth?.access_token ?? "";
  const { hasRole } = useRealmRoles();

  const [order, setOrder] = useState<Order | null>(null);
  const [model, setModel] = useState<OrderModelResponse | null>(null);
  const [config, setConfig] = useState<FormRootConfig | null>(null);

  const [loading, setLoading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [modelError, setModelError] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSubmitting, setHelpSubmitting] = useState(false);

  const loadOrder = async () => {
    setLoading(true);
    setOrderError(null);
    setModelError(null);
    setConfigError(null);

    Promise.allSettled([
      fetchOrder(orderId, accessToken),
      fetchOrderModel(orderId, accessToken),
      fetchOrderConfig(orderId, accessToken)
    ])
      .then(([orderRes, modelRes, configRes]) => {
        if (orderRes.status === 'fulfilled') setOrder(orderRes.value);
        else setOrderError(orderRes.reason?.message ?? String(orderRes.reason ?? 'Ошибка получения данных по заявке'));

        if (modelRes.status === 'fulfilled') setModel(modelRes.value);
        else setModelError(modelRes.reason?.message ?? String(modelRes.reason ?? 'Ошибка получения расширенных данных по заявке'));

        if (configRes.status === 'fulfilled') setConfig(configRes.value);
        else setConfigError(configRes.reason?.message ?? String(configRes.reason ?? 'Ошибка получения расширенных данных по заявке'));
      })
      .finally(() => setLoading(false));
  }

  const handleHelpOk = async () => {
    setHelpSubmitting(true);
    try {
      await requestHelpByOrder(orderId, accessToken);
      await new Promise((res) => setTimeout(res, 1000)); // Ждём пока процесс проинициализируется
      message.success('Запрос помощи консультанта отправлен');
      setHelpOpen(false);
      await loadOrder();
    } catch (e: any) {
      message.error('Не удалось отправить запрос. Попробуйте ещё раз');
      console.error('Failed to request help by order: ' + e?.message);
    } finally {
      setHelpSubmitting(false);
    }
  };

  const handleHelpCancel = () => setHelpOpen(false);

  const canRequestHelp = hasRole("USE_ADVOCATE_ENABLED") &&
    !model?.components?.bgBankOrders?.orders?.some(x => x.bankInn === 'advocate');
  const requestHelp = canRequestHelp ?
    () => setHelpOpen(true) :
    undefined;

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setModel(null);
      setOrderError(null);
      setModelError(null);
      setConfigError(null);
      return;
    }

    (async () => await loadOrder())();
  }, [orderId]);

  const bg = model?.components?.bgBankOrders;

  if (!accessToken) return <div className="row muted">Нет подключения</div>;
  if (loading) return <div className="row muted">Загрузка данных заявки…</div>;

  return (
    <OrderFormContext.Provider value={{ order, model, config, reloadOrder: loadOrder, requestHelp: requestHelp }}>
      {/* Шапка с номером и статусом */}
      <Row justify="space-between" align="top" gutter={16}>
        <Col>
          {order && <OrderHeader order={order} />}
        </Col>
        <Col>
          <Space size="middle" align="start">
            {model?.components?.principal && (
              <CustomerLink data={model.components.principal} />
            )}
            {model?.components?.productBgCard && (
              <ProductBgLink data={model.components.productBgCard} />
            )}
          </Space>
        </Col>
      </Row>

      {/* Таблица с банковскими заявками */}
      {bg ? (
        <OrderInfoTabs />
      ) : (
        <div className="row muted">Модель заявки не загружена</div>
      )}
      {orderError && <div className="row err">Ошибка (order): {orderError}</div>}
      {modelError && <div className="row err">Ошибка (model): {modelError}</div>}
      {configError && <div className="row err">Ошибка (config): {configError}</div>}

      <Modal
        title="Подтверждение"
        open={helpOpen}
        onOk={handleHelpOk}
        onCancel={handleHelpCancel}
        okText="Запросить помощь"
        cancelText="Отмена"
        confirmLoading={helpSubmitting}
        destroyOnClose
      >
        <p>Вы хотите запросить помощь консультанта для получения банковской гарантии?</p>
      </Modal>
    </OrderFormContext.Provider>
  );
}
